import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useEffect, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { removeWebsiteLocale } from 'src/editor/api/website-websites-api'
import { ConfirmationDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/confirmation-dialog-window'
import {
  setCurrentWebsiteLocale,
  setWebsiteLocales,
  removeWebsiteLayout,
} from 'src/editor/store/website-settings/website-settings-actions'
import {
  getCurrentWebsiteLocale,
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'

interface RemoveWebsiteLocaleProps {
  localeToRemove: PageLocaleEnum | null
  onClose: () => void
}

export const RemoveWebsiteLocale = ({
  localeToRemove,
  onClose,
}: RemoveWebsiteLocaleProps) => {
  const { t } = useTypedTranslation()
  const [isOpened, setIsOpened] = useState(false)
  const dispatch = useAppDispatch()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocales = useWebsiteSettings(getWebsiteLocales) || []
  const websiteLocale = useWebsiteSettings(getWebsiteLocale) as PageLocaleEnum
  const localesWithoutRemovedLocale = websiteLocales.filter(
    l => l !== localeToRemove,
  )
  const currentWebsiteLocale = useWebsiteSettings(
    getCurrentWebsiteLocale,
  ) as PageLocaleEnum

  const handleClose = () => {
    setIsOpened(false)
    onClose()
  }

  const { fetcher, isLoading } = useMutationWithValidation({
    requestFunction: removeWebsiteLocale,
    successToastMessage: t(
      'website_settings.remove_locale.removed_successfully',
    ),
    handleSuccess: () => {
      dispatch(setWebsiteLocales(localesWithoutRemovedLocale))

      if (localeToRemove) {
        dispatch(removeWebsiteLayout(localeToRemove))
      }

      if (currentWebsiteLocale === localeToRemove) {
        dispatch(setCurrentWebsiteLocale(websiteLocale))
      }
    },
    handleFinally: () => {
      handleClose()
    },
    invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
  })

  useEffect(() => {
    if (localeToRemove) {
      setIsOpened(true)
    }
  }, [localeToRemove])

  const handleRemoveLocale = () => {
    if (localeToRemove) {
      fetcher({
        id: websiteId,
        locale: localeToRemove,
      })
    }
  }

  return (
    <ConfirmationDialogWindow
      isOpened={isOpened}
      onClose={handleClose}
      title={t('website_settings.remove_locale.confirmation_window.title')}
      description={t(
        'website_settings.remove_locale.confirmation_window.description',
      )}
      cancelButtonProps={{
        children: t('global.cancel'),
        onClick: handleClose,
      }}
      confirmButtonProps={{
        isLoading,
        onClick: handleRemoveLocale,
        children: t('global.remove'),
      }}
    />
  )
}
